// Rolling numbers on the cart page and My Deals. WooCommerce and my-deals.js replace the cart
// form, the totals and the collection cards wholesale after every change, so nothing repaints
// a number in place: the shown numbers are remembered, and after a replacement every number
// that differs rolls from its old value (price-roll.js). In the cart only the line price
// rolls. Personal-price nodes roll on their own and are left out here. Cart suggestions do
// not roll: when their products change, the whole block fades in instead.

import { numberNode, rollNumber, shownNumber } from './price-roll';

const ROOT = '.woocommerce';
const SUGGESTIONS = '.son-cart-suggestions';
const PRICES = [
    'td.product-price .woocommerce-Price-amount bdi',
    '.son-my-deals-card__badge',
    '.son-my-deals-item__price',
    '.son-my-deals-best__price del',
    '.son-my-deals-best__price ins',
].join(',');
// counts roll only the digits that change, no full lap ("3 of 5": the 5 stays)
const COUNTS = [
    '.son-my-deals-card__remaining',
    '.son-my-deals-card__owned',
].join(',');
const NUMBERS = `${PRICES},${COUNTS}`;

let shown = new Map();
let suggested = '';

// What identifies a number across a replacement: the cart line, totals row, collection card
// and card row it sits in, and whether it is the struck-through amount.
function scope(el) {
    const parts = [];
    for (let node = el; node && !node.matches?.(ROOT); node = node.parentElement) {
        if (node.dataset?.sonMyDealsCard) {
            parts.push(`card${node.dataset.sonMyDealsCard}`);
        } else if (node.matches('tr, li')) {
            const id = node.querySelector('[data-son-my-deals-check]')?.dataset.sonMyDealsCheck
                ?? node.querySelector('input[name^="cart["]')?.name;
            parts.push(id ?? node.className.split(' ')[0]);
        } else if (node.matches('td, th')) {
            parts.push(node.className.split(' ')[0]);
        }
    }

    return `${parts.reverse().join('/')}/${el.className.split(' ')[0]}${el.closest('del') ? '/del' : ''}`;
}

function numbers() {
    const root = document.querySelector(ROOT);
    if (!root) {
        return [];
    }

    return [...root.querySelectorAll(NUMBERS)].filter((el) => !el.closest(`.son-personal-price, ${SUGGESTIONS}`));
}

// the suggested products, in order
function suggestions() {
    return [...document.querySelectorAll(`${ROOT} ${SUGGESTIONS} [data-product_id]`)]
        .map((el) => el.dataset.product_id)
        .join(',');
}

// the same key twice (two amounts in one cell) gets a running index
function keyed() {
    const seen = new Map();

    return numbers().map((el) => {
        const key = scope(el);
        const n = seen.get(key) ?? 0;
        seen.set(key, n + 1);

        return [`${key}#${n}`, el];
    });
}

function remember() {
    shown = new Map(keyed().map(([key, el]) => [key, shownNumber(el)]));
    suggested = suggestions();
}

function roll() {
    const before = shown;
    if (suggestions() !== suggested) {
        document.querySelector(`${ROOT} ${SUGGESTIONS}`)?.classList.add('is-new');
    }
    keyed().forEach(([key, el]) => {
        const textNode = numberNode(el);
        // still mid-roll from the last change: it already shows its new value
        if (textNode) {
            rollNumber(textNode, before.get(key), textNode.parentElement, !el.matches(COUNTS));
        }
    });
    remember();
}

function init() {
    const root = document.querySelector(ROOT);
    if (!root || !(document.body.classList.contains('woocommerce-cart') || root.querySelector('.son-my-deals'))) {
        return;
    }
    remember();

    // one pass per frame however many nodes a replacement touches; our own rolls change the
    // DOM too, but then every number already shows its remembered value
    let queued = false;
    new MutationObserver(() => {
        if (queued) {
            return;
        }
        queued = true;
        requestAnimationFrame(() => {
            queued = false;
            roll();
        });
    }).observe(root, { childList: true, subtree: true });
}

document.addEventListener('DOMContentLoaded', init);
